import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Navigate, useNavigate, useLocation, type NavigateFunction } from 'react-router-dom';
import { useDashboard } from '../contexts/DashboardContext';
import { useTopBarTitle, useTopBarTitleNode, useTopBarBreadcrumbs, useTopBarActions } from '../contexts/TopBarContext';
import Dashboard from '../components/Dashboard';
import { DashboardGenerationView, DashboardLayoutSkeleton } from '../components/dashboard/skeletons';
import { dashboardsService } from '../api/dashboardsService';
import type { Dashboard as DashboardDetail } from '../api/types';
import {
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import InlineEditableTitle from '../components/InlineEditableTitle';
import { MutationButton, useWorkspaceAccess } from '../components/security/WorkspaceAccess';

/**
 * Dashboard View Page
 * Loads and displays a specific dashboard based on URL parameter.
 *
 * Detects dashboard status:
 * - "generating" → shows CreationProgress with polling
 * - "failed"     → shows error state with retry option
 * - "ready"      → shows normal dashboard
 */
/** Navigation state passed from DashboardCreation to skip the loading spinner. */
interface GeneratingNavState {
  generating?: boolean;
  jobId?: string;
}

const DashboardViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state as GeneratingNavState | undefined;
  const { runMutation } = useWorkspaceAccess();
  const {
    currentDashboard,
    currentDashboardDetails,
    setCurrentDashboard,
    error,
    refreshDashboards,
    loadDashboardDetails,
    deleteDashboard,
    updateDashboardInList,
  } = useDashboard();

  const [isCancelling, setIsCancelling] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);

  // ── Regenerate handler ────────────────────────────────────────────────
  // Mutates local dashboardDetail to switch view to generating mode
  // without navigating. Single source of truth: dashboardDetail drives
  // which view is rendered.
  const handleRegenerate = useCallback(() => {
    if (!id) return Promise.resolve();
    return runMutation(async () => {
      setIsRegenerating(true);
      try {
        const result = await dashboardsService.regenerate(id);
        // Mutate local state → triggers DashboardGenerationView branch
        setDashboardDetail((prev) => ({
          ...(prev as DashboardDetail),
          status: 'generating' as DashboardDetail['status'],
          jobId: result.jobId,
        }));
        // Keep sidebar/context in sync
        updateDashboardInList(id, { status: 'generating', jobId: result.jobId });
        setIsCancelling(false);
      } catch (err) {
        console.error('Failed to regenerate dashboard:', err);
      } finally {
        setIsRegenerating(false);
      }
    }) ?? Promise.resolve();
  }, [id, runMutation, updateDashboardInList]);

  // Fetch the raw dashboard detail for status detection.
  // Falls back to context details (which also now includes status) when
  // the direct fetch hasn't resolved yet — avoids empty screen on nav back.
  const [dashboardDetail, setDashboardDetail] = useState<DashboardDetail | null>(null);

  // Resolve status from multiple sources (priority order):
  // 1. Direct API fetch (dashboardDetail)
  // 2. Context detail (currentDashboardDetails)
  // 3. Context list-level data (currentDashboard — has status/jobId from list)
  // 4. Navigation state from DashboardCreation (navState)
  const effectiveDetail = dashboardDetail ?? (currentDashboardDetails as unknown as DashboardDetail | null);
  const dashboardStatus =
    effectiveDetail?.status ??
    currentDashboard?.status ??
    (navState?.generating ? 'generating' : undefined);
  const jobId =
    effectiveDetail?.jobId ??
    currentDashboard?.jobId ??
    navState?.jobId;

  // ── Stable callback handlers (memoised to prevent useTopBarActions loop) ─
  const handleComplete = useCallback(async () => {
    await refreshDashboards();
    if (id) {
      await loadDashboardDetails(id);
      const updated = await dashboardsService.getDashboard(id);
      setDashboardDetail(updated);
    }
  }, [id, refreshDashboards, loadDashboardDetails]);

  const handleCancel = useCallback(() => {
    if (!jobId) return;
    return runMutation(async () => {
      setIsCancelling(true);
      try {
        await dashboardsService.cancelJob(jobId);
      } catch (err) {
        console.error('Failed to cancel job:', err);
      }
    });
  }, [jobId, runMutation]);

  const handleCancelled = useCallback(async () => {
    setIsCancelling(false);
    // Re-fetch dashboard detail so status becomes "canceled"
    // and the page renders CanceledDashboardView in-place.
    if (id) {
      try {
        const updated = await dashboardsService.getDashboard(id);
        // Guard: if the worker hasn't finalized yet the DB may still
        // say "cancel_requested". Force "canceled" so we don't loop
        // back into the generation view.
        if (updated.status === 'cancel_requested') {
          updated.status = 'canceled' as DashboardDetail['status'];
        }
        setDashboardDetail(updated);
        // Keep sidebar/context in sync
        updateDashboardInList(id, { status: 'canceled' });
      } catch (err) {
        console.error('Failed to refetch dashboard after cancel:', err);
        // Fallback: force canceled status locally
        setDashboardDetail((prev) =>
          prev ? { ...prev, status: 'canceled' as DashboardDetail['status'] } : prev,
        );
        updateDashboardInList(id, { status: 'canceled' });
      }
    }
  }, [id, updateDashboardInList]);

  const handleFailed = useCallback(async () => {
    // Re-fetch dashboard detail so status becomes "failed"
    // and the page renders StoppedDashboardView in-place.
    if (id) {
      try {
        const updated = await dashboardsService.getDashboard(id);
        setDashboardDetail(updated);
        updateDashboardInList(id, { status: 'failed' });
      } catch (err) {
        console.error('Failed to refetch dashboard after failure:', err);
        // Fallback: force failed status locally
        setDashboardDetail((prev) =>
          prev ? { ...prev, status: 'failed' as DashboardDetail['status'] } : prev,
        );
        updateDashboardInList(id, { status: 'failed' });
      }
    }
  }, [id, updateDashboardInList]);

  const handleRetry = useCallback(() => {
    navigate('/dashboards', { replace: true });
  }, [navigate]);

  useEffect(() => {
    setDashboardDetail(null);
    if (!id) return;
    dashboardsService.getDashboard(id).then(setDashboardDetail).catch((err) => {
      console.error('Failed to fetch dashboard detail:', err);
    });
  }, [id]);

  // Also update when context details change (e.g. after generation completes)
  useEffect(() => {
    if (currentDashboardDetails && (currentDashboardDetails as unknown as DashboardDetail).status) {
      setDashboardDetail(currentDashboardDetails as unknown as DashboardDetail);
    }
  }, [currentDashboardDetails]);

  // ── Inline editable title ───────────────────────────────────────────────
  const handleTitleSave = useCallback((newName: string) => {
    if (!id) return;
    return runMutation(async () => {
      try {
        await dashboardsService.updateDashboard(id, { name: newName });
        updateDashboardInList(id, { name: newName });
      } catch (err) {
        console.error('Failed to rename dashboard:', err);
      }
    });
  }, [id, runMutation, updateDashboardInList]);

  const dashboardName = currentDashboard?.name || 'Dashboard';

  const titleNode = useMemo(
    () => (
      <InlineEditableTitle
        value={dashboardName}
        onSave={handleTitleSave}
        placeholder="Untitled Dashboard"
      />
    ),
    [dashboardName, handleTitleSave],
  );

  // Set TopBar with editable title
  useTopBarTitle(dashboardName); // fallback for screen readers / document.title
  useTopBarTitleNode(titleNode);
  useTopBarBreadcrumbs([
    { text: 'Dashboards', to: '/dashboards' },
    { text: dashboardName },
  ]);

  // Load dashboard when ID changes
  useEffect(() => {
    if (id) {
      setCurrentDashboard(id);
    }
  }, [id, setCurrentDashboard]);

  // Redirect to dashboards list if no ID
  if (!id) {
    return <Navigate to="/dashboards" replace />;
  }

  // Show loading state — only when neither dashboardDetail nor context has
  // data yet AND we don't have generating state from navigation.
  if (!effectiveDetail && dashboardStatus !== 'generating') {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <Spinner size="large" label="Loading dashboard..." />
      </div>
    );
  }

  // Show error state
  if (error && dashboardStatus !== 'generating' && dashboardStatus !== 'failed') {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          <MessageBarTitle>Error Loading Dashboard</MessageBarTitle>
          {error}
        </MessageBarBody>
      </MessageBar>
    );
  }

  // ── Status: generating or cancel_requested → show skeleton dashboard ────
  if ((dashboardStatus === 'generating' || dashboardStatus === 'cancel_requested') && jobId) {
    return (
      <DashboardGenerationView
        jobId={jobId}
        dashboardId={id}
        dashboardName={effectiveDetail?.name || ''}
        onComplete={handleComplete}
        onCancel={handleCancel}
        onCancelled={handleCancelled}
        onFailed={handleFailed}
        onRetry={handleRetry}
        isCancelling={isCancelling || dashboardStatus === 'cancel_requested'}
      />
    );
  }

  // Generating but no jobId — shouldn't happen, show a loading state
  if (dashboardStatus === 'generating' || dashboardStatus === 'cancel_requested') {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <Spinner size="large" label={dashboardStatus === 'cancel_requested' ? 'Canceling...' : 'Loading dashboard...'} />
      </div>
    );
  }

  // ── Status: canceled → show canceled state ──────────────────────────────
  if (dashboardStatus === 'canceled') {
    return (
      <StoppedDashboardView
        id={id}
        intent="warning"
        title="Dashboard creation canceled"
        onRegenerate={handleRegenerate}
        isRegenerating={isRegenerating}
        deleteDashboard={deleteDashboard}
        navigate={navigate}
      />
    );
  }

  // ── Status: failed → show error with retry ─────────────────────────────
  if (dashboardStatus === 'failed') {
    return (
      <StoppedDashboardView
        id={id}
        intent="error"
        title="Dashboard generation failed"
        body="Something went wrong. Please try again."
        regenerateLabel="Try Again"
        onRegenerate={handleRegenerate}
        isRegenerating={isRegenerating}
        deleteDashboard={deleteDashboard}
        navigate={navigate}
      />
    );
  }

  // ── Status: ready → render normal dashboard ─────────────────────────────
  return <Dashboard />;
};

// ═══════════════════════════════════════════════════════════════════════════
// StoppedDashboardView — reusable for canceled & failed states
//
// Renders a banner (warning or error) + top bar actions + frozen skeleton.
// ═══════════════════════════════════════════════════════════════════════════

interface StoppedDashboardViewProps {
  id: string;
  /** MessageBar intent — "warning" for canceled, "error" for failed. */
  intent: 'warning' | 'error';
  /** Banner title text. */
  title: string;
  /** Optional banner body text (displayed below the title). */
  body?: string;
  /** Label for the primary regenerate button. Defaults to "Regenerate". */
  regenerateLabel?: string;
  onRegenerate: () => Promise<void>;
  isRegenerating: boolean;
  deleteDashboard: (id: string) => Promise<any>;
  navigate: NavigateFunction;
}

const StoppedDashboardView: React.FC<StoppedDashboardViewProps> = ({
  id,
  intent,
  title,
  body,
  regenerateLabel = 'Regenerate',
  onRegenerate,
  isRegenerating,
  deleteDashboard,
  navigate,
}) => {
  const { runMutation } = useWorkspaceAccess();

  const handleDelete = useCallback(() =>
    runMutation(async () => {
      try {
        await deleteDashboard(id);
        navigate('/dashboards', { replace: true });
      } catch (err) {
        console.error('Failed to delete dashboard:', err);
      }
    }), [id, deleteDashboard, navigate, runMutation]);

  const topBarActions = useMemo(
    () => (
      <div style={{ display: 'flex', gap: '8px' }}>
        <MutationButton appearance="outline" onClick={handleDelete} disabled={isRegenerating}>
          Delete
        </MutationButton>
        <MutationButton appearance="primary" onClick={onRegenerate} disabled={isRegenerating}>
          {isRegenerating ? 'Regenerating…' : regenerateLabel}
        </MutationButton>
      </div>
    ),
    [onRegenerate, isRegenerating, handleDelete, regenerateLabel],
  );
  useTopBarActions(topBarActions);

  return (
    <div className="dashboard-generation-view">
      {/* Banner */}
      <div style={{ padding: '12px 16px', flexShrink: 0 }}>
        <MessageBar intent={intent}>
          <MessageBarBody>
            <MessageBarTitle>{title}</MessageBarTitle>
            {body}
          </MessageBarBody>
        </MessageBar>
      </div>

      {/* Frozen skeleton layout */}
      <div className="dashboard-generation-view__content">
        <DashboardLayoutSkeleton frozen />
      </div>
    </div>
  );
};

export default DashboardViewPage;
